/**
 * Logo.jsx — the EduGate brand mark.
 *
 * [ICONS-2026-09] The app used to render a generic lucide `GraduationCap`
 * wherever a logo belonged, so the installed icon and the in-app brand were
 * two different drawings. This is the same geometry as the icon set in
 * `public/icons/` (built by `scripts/generate-icons.js`) — a gateway arch
 * framing a mortarboard: "Edu" + "Gate".
 *
 * Kept as inline SVG rather than an <img> so it inherits crispness at any
 * size, needs no network round-trip on first paint, and can be recoloured.
 *
 * If you change the artwork here, change it in scripts/generate-icons.js too
 * and re-run that script — otherwise the installed icon drifts from the UI.
 */
import React from 'react';

/**
 * @param {object}  props
 * @param {string}  props.className  sizing/positioning classes for the <svg>
 * @param {boolean} props.plate      draw the brand gradient plate behind the
 *                                   mark (the app-icon look). When false the
 *                                   mark is drawn in `currentColor`, for
 *                                   placing on a coloured surface.
 * @param {string}  props.title      accessible name; pass "" to mark decorative
 * @param {object}  props.style      inline styles forwarded to the <svg>
 */
export default function Logo({ className = 'w-10 h-10', plate = true, title = 'EduGate', style }) {
  const gid = React.useId();
  return (
    <svg
      viewBox="0 0 512 512"
      className={className}
      style={style}
      role={title ? 'img' : 'presentation'}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : 'true'}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {plate && (
        <>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#2DD4BF" />
              <stop offset="48%" stopColor="#0F766E" />
              <stop offset="100%" stopColor="#0A4F4A" />
            </linearGradient>
          </defs>
          <rect width="512" height="512" rx="112" fill={`url(#${gid})`} />
        </>
      )}
      <g fill={plate ? '#ffffff' : 'currentColor'}>
        <path
          d="M104 412 L104 268 a152 152 0 0 1 304 0 L408 412"
          fill="none"
          stroke={plate ? '#ffffff' : 'currentColor'}
          strokeOpacity="0.32"
          strokeWidth="24"
          strokeLinecap="round"
        />
        <path d="M256 172 L384 224 L256 276 L128 224 Z" />
        <path d="M192 248 L192 292 a64 30 0 0 0 128 0 L320 248 L256 274 Z" fillOpacity={plate ? 0.9 : 0.75} />
        <path
          d="M366 232 L366 306"
          fill="none"
          stroke={plate ? '#ffffff' : 'currentColor'}
          strokeWidth="13"
          strokeLinecap="round"
        />
        <circle cx="366" cy="324" r="17" />
      </g>
    </svg>
  );
}
